// frontend/src/utils/slotConflictApi.js
//
// Part B: the single place the customer-facing UI acknowledges a slot-conflict
// placement ("Keep this time"). Shared so the post-payment return page and My
// Bookings cannot drift apart in how they call it or how they read the result.

const getApiBaseUrl = () =>
  String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

/**
 * Acknowledge the time we held for the customer.
 *
 * @param {string} bookingId
 * @returns {Promise<object>} the updated booking fragment from the API
 * @throws {Error} with a customer-safe message
 */
export async function keepSlotConflictTime(bookingId) {
  const token = localStorage.getItem("ogestyle_token");

  if (!bookingId) {
    throw new Error("We could not identify this appointment.");
  }

  if (!token) {
    throw new Error(
      "Please sign in to keep this time, or use the link in your email.",
    );
  }

  const response = await fetch(
    `${getApiBaseUrl()}/api/bookings/${bookingId}/slot-conflict/keep`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.message || "Unable to keep this time");
  }

  return data?.booking || {};
}

/**
 * Mint a reschedule token for a signed-in customer looking at their own booking,
 * so the slot-conflict page opens directly on the picker instead of asking them
 * for a booking number, email and phone digits they should not have to re-enter.
 *
 * @param {string} bookingId
 * @returns {Promise<{token: string}>}
 */
export async function requestSlotConflictRescheduleToken(bookingId) {
  const token = localStorage.getItem("ogestyle_token");

  if (!bookingId) {
    throw new Error("We could not identify this appointment.");
  }

  if (!token) {
    throw new Error("not_signed_in");
  }

  const response = await fetch(
    `${getApiBaseUrl()}/api/bookings/${bookingId}/slot-conflict/reschedule-token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.token) {
    throw new Error(data?.message || "Unable to open the time picker");
  }

  return { token: data.token };
}

/**
 * True when this booking is sitting in a slot conflict the customer has not
 * answered yet.
 */
export function hasOpenSlotConflict(booking) {
  const conflict = booking?.slotConflict;
  if (!conflict || !conflict.outcome) return false;
  if (conflict.resolution && conflict.resolution !== "awaiting_customer") {
    return false;
  }
  return true;
}
